import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { RecipeSummary } from '@fewerbags/schema';
import { timeSummary } from '../../pages/recipes/recipe-form';
import { ClockIcon, UsersIcon } from '../icons';
import { CuisineCourseBadges } from './CuisineCourseBadges';
import { RecipePhoto } from './RecipePhoto';
import { RatingSummary } from './StarRating';

/**
 * Grid card: large photo (or generated placeholder) on top, then title,
 * time/servings, cuisine/course badges, rating and up to three tags. The
 * whole card is one link target via the title's stretched ::after; `action`
 * (e.g. "Copy to our recipes") sits above it in its own footer.
 */
export function RecipeCard({ recipe, action }: { recipe: RecipeSummary; action?: ReactNode }) {
  const times = timeSummary(recipe.prepMin, recipe.cookMin);
  return (
    <li className="group card card-link relative flex flex-col overflow-hidden has-[h3_a:focus-visible]:outline-2 has-[h3_a:focus-visible]:outline-offset-2 has-[h3_a:focus-visible]:outline-brand">
      <div className="relative aspect-[16/9] overflow-hidden bg-brand-soft sm:aspect-[4/3]">
        <RecipePhoto
          photoMediaId={recipe.photoMediaId}
          title={recipe.title}
          variant="thumb"
          courses={recipe.courses}
          cuisines={recipe.cuisines}
          className="size-full transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:group-hover:scale-100"
        />
        {recipe.visibility === 'public' ? (
          <span className="absolute top-2.5 right-2.5 rounded-full bg-surface/95 px-2.5 py-0.5 text-xs font-semibold text-accent-ink shadow-sm">
            Shared
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="text-base leading-snug sm:text-lg">
          <Link
            to={`/recipes/${recipe.id}`}
            className="after:absolute after:inset-0 after:content-[''] focus-visible:shadow-none focus-visible:outline-none"
          >
            {recipe.title}
          </Link>
        </h3>
        {recipe.description ? (
          <p className="line-clamp-2 text-sm text-muted">{recipe.description}</p>
        ) : null}
        {times || recipe.servings ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
            {times ? (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="size-3.5" />
                {times}
              </span>
            ) : null}
            {recipe.servings ? (
              <span className="inline-flex items-center gap-1">
                <UsersIcon className="size-3.5" />
                serves {recipe.servings}
              </span>
            ) : null}
          </p>
        ) : null}
        <CuisineCourseBadges cuisines={recipe.cuisines} courses={recipe.courses} size="xs" />
        <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
          <RatingSummary avg={recipe.rating.avg} count={recipe.rating.count} />
          {recipe.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-full bg-brand-soft px-2 py-0.5 text-xs text-brand">
              {tag}
            </span>
          ))}
        </div>
      </div>
      {action ? (
        <div className="relative z-[1] border-t border-line bg-cream/40 px-4 py-2">{action}</div>
      ) : null}
    </li>
  );
}
